<template>
	<div class="container_section p-5">
		<div class="table_header is-flex is-justify-content-space-between is-align-items-center mb-4">
			<div class="is-flex is-align-items-center">
				<Ecs class="mr-2" style="width: 20px; height: 20px;" />
				<span class="tittle_section">Evolución de Ventas Netas</span>
			</div>
			<div class="table_actions is-flex">
				<button class="action_btn mr-2">
					<ChartLine class="mr-1" style="width: 16px; height: 16px;" />
					Comparación
				</button>
				<button class="action_btn mr-2">
					<ChartLine class="mr-1" style="width: 16px; height: 16px;" />
					Ver Gráfico
				</button>
				<div v-if="showPeriodSelect" style="min-width: 140px;">
					<SelectComponent :value="selectedPeriod" theme="report_theme"
						:options="periodOptions"
						@input="$emit('change-period', $event)" />
				</div>
			</div>
		</div>

		<section>
			<div v-if="isLoading" class="loading_state py-6 has-text-centered">
				<p>Cargando datos...</p>
			</div>

			<div v-else-if="error" class="error_state py-6 has-text-centered">
				<p class="has-text-danger">{{ error }}</p>
				<button class="action_btn mt-3" @click="$emit('retry')">Reintentar</button>
			</div>

			<div v-else-if="monthHeaders.length === 0" class="empty_state py-6 has-text-centered">
				<p>No hay datos de ventas para el período seleccionado.</p>
			</div>

			<div v-else class="table_container" ref="mainTable">
				<table class="custom_table"
					:style="{ '--sticky-col-1-width': firstColWidth + 'px' }">
					<colgroup>
						<col style="width: 200px;" />
						<col style="width: 160px;" />
						<col style="width: 160px;" v-for="header in monthHeaders" :key="header" />
					</colgroup>
					<thead>
						<tr>
							<th ref="firstStickyCell" scope="col" class="sticky-column header_label nobreak"></th>
							<th scope="col"
								class="sticky-column sticky-column--acc header_label has-text-right nobreak">
								{{ periodOptions.find(opt => opt.value === selectedPeriod).label }}
							</th>
							<th scope="col" class="has-text-right nobreak is-capitalized"
								v-for="header in monthHeaders" :key="header">
								{{ header }}
							</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in tableRows" :key="rowKey(row)"
							:class="['theme_row_white', { 'row-level-2': row.level === 2, 'row-level-3': row.level === 3 }]">
							<th scope="row"
								:class="['sticky-column nobreak theme_row_white is-capitalized', row.level === 3 ? 'pl-5' : '']">
								<div class="is-flex is-align-items-center is-justify-content-space-between label_inner">
									<div class="is-flex is-align-items-center label_cell">
										<span v-if="row.level === 3" class="indent-symbol mr-1">↳</span>
										<span class="label_text"
											v-tooltip.right="row.label.length > 30 ? row.label : ''">
											{{ truncateLabel(row.label) }}
										</span>
									</div>
									<!-- Chevron solo en L2 — toggle aislado al chevron (decisión UX:
										no clickeable el row entero para no robar el click de drill-
										down de las celdas de monto). -->
									<button v-if="row.level === 2"
										type="button"
										class="chevron-btn"
										:aria-label="expandedL2Rows.includes(row.label) ? 'Colapsar' : 'Expandir'"
										:aria-expanded="String(expandedL2Rows.includes(row.label))"
										@click="$emit('toggle-l2', row.label)">
										<ArrowDown class="expand-icon"
											:class="{ 'rotate-icon': expandedL2Rows.includes(row.label) }" />
									</button>
								</div>
							</th>
							<td class="sticky-column sticky-column--acc has-text-right nobreak acc_cell theme_row_white"
								v-tooltip.top="growthTooltip(row.pct)">
								<span class="acc_amount">{{ parseToMoney(row.accumulated) }}</span>
								<span class="acc_pct acc_pct--neutral">
									{{ formatShare(row.accumulated) }}
								</span>
							</td>
							<td v-for="(val, idx) in row.values" :key="idx"
								class="has-text-right nobreak theme_row_white"
								:class="{ clickable_cell: cellsClickable }"
								@click="onCellClick(monthHeaders[idx], row)">
								{{ parseToMoney(val) }}
							</td>
						</tr>

						<tr class="theme_row_azure">
							<th scope="row" class="sticky-column nobreak theme_row_azure">
								{{ totalRow.label }}
							</th>
							<td class="sticky-column sticky-column--acc has-text-right nobreak acc_cell theme_row_azure"
								v-tooltip.top="growthTooltip(totalRow.pct)">
								<span class="acc_amount">{{ parseToMoney(totalRow.accumulated) }}</span>
							</td>
							<td v-for="(val, idx) in totalRow.values" :key="'total-' + idx"
								class="has-text-right nobreak theme_row_azure"
								:class="{ clickable_cell: cellsClickable }"
								@click="onCellClick(monthHeaders[idx], null)">
								{{ parseToMoney(val) }}
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</section>
	</div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";
import { formatVariation } from "@/utils/percentage";
import SelectComponent from "@/components/classification/SelectComponent";
import Ecs from "@/components/svg/Ecs.vue";
import ArrowDown from "@/components/svg/ArrowDown.vue";
import ChartLine from "@/components/svg/ChartLine.vue";

export default {
	name: "SalesEvolutionTable",

	components: { SelectComponent, Ecs, ArrowDown, ChartLine },

	props: {
		monthHeaders: { type: Array, required: true },
		// Filas a renderizar (el caller las filtra por expandedL2Rows en modo
		// jerárquico). En modo plano cada fila es { label, values, accumulated,
		// pct }; en jerárquico viene además { level: 2|3, parent? }.
		tableRows: { type: Array, required: true },
		totalRow: { type: Object, required: true },
		// Labels de las cuentas L2 actualmente expandidas. Solo lo consulta el
		// chevron para rotar — el filtrado de visibilidad de hijos lo hace el
		// caller para que sea él quien decida el contrato (mismo patrón que
		// ExpensesEvolutionTable).
		expandedL2Rows: { type: Array, default: () => [] },
		isLoading: { type: Boolean, default: false },
		error: { type: String, default: "" },
		selectedPeriod: { type: String, required: true },
		periodOptions: { type: Array, required: true },
		// Granularidad activa (month/quarter/year/ytd). Cuando es year/ytd el
		// rango ya es implícito y ocultamos el selector de períodos — mismo
		// criterio que FinancialResultsTable.
		filterPeriod: { type: String, default: "month" },
	},

	computed: {
		showPeriodSelect() {
			return !["ytd", "year"].includes(this.filterPeriod);
		},
		// Modal de detalle: solo aplica a granularidad mensual. Para quarter/
		// year/ytd una "celda" agregaría varios meses y el modal pierde sentido.
		cellsClickable() {
			return this.filterPeriod === "month";
		},
	},

	data() {
		// Fallback inicial igual al <col width> de la primera columna para evitar
		// "flash" antes de la primera medición. Se sobrescribe en mounted().
		return { firstColWidth: 200 };
	},

	mounted() {
		// La primera medición y el observer corren después del render para que
		// el thead exista. ResizeObserver dispara también con cambios indirectos
		// (filtros, scrollbars, zoom del navegador) sin necesidad de watchers.
		this.$nextTick(() => {
			this.measureFirstCol();
			this.attachResizeObserver();
		});
	},

	beforeDestroy() {
		if (this._resizeObserver) {
			this._resizeObserver.disconnect();
			this._resizeObserver = null;
		}
	},

	methods: {
		parseToMoney,
		truncateLabel(label) {
			return label.length > 30 ? label.substring(0, 30) + "..." : label;
		},
		// Key estable y única: en modo jerárquico el mismo cliente puede
		// aparecer bajo dos cuentas distintas (mismo `label`) — combinar con
		// parent evita warnings de v-for y mantiene el DOM-diff correcto al
		// expandir/colapsar.
		rowKey(row) {
			if (row.level === 3) return `${row.parent}|${row.label}`;
			return row.label;
		},
		scrollToRight() {
			this.$nextTick(() => {
				const table = this.$refs.mainTable;
				if (table) table.scrollLeft = table.scrollWidth;
			});
		},
		// Lee el ancho real de la primera columna y lo expone como CSS var en
		// la <table>; .sticky-column--acc usa esa var como offset `left`, así
		// la segunda sticky queda pegada al borde derecho de la primera sin
		// importar cuánto crezca el contenido de la cuenta.
		measureFirstCol() {
			const cell = this.$refs.firstStickyCell;
			if (cell) this.firstColWidth = cell.offsetWidth;
		},
		attachResizeObserver() {
			if (typeof ResizeObserver === "undefined") return;
			const cell = this.$refs.firstStickyCell;
			if (!cell) return;
			// Disconnect previo: v-if del table_container desmonta/remonta el
			// thead, así que el ref puede apuntar a un nodo nuevo entre llamadas.
			if (this._resizeObserver) this._resizeObserver.disconnect();
			this._resizeObserver = new ResizeObserver(() => this.measureFirstCol());
			this._resizeObserver.observe(cell);
		},
		formatPct(pct) {
			return formatVariation(pct, { withArrow: true, withSign: true });
		},
		// Share del acumulado de la fila sobre el total general (última fila).
		// Sin arrow ni sign: es magnitud, no variación. `formatVariation` espera
		// un ratio (no porcentaje), por eso dividimos sin multiplicar por 100.
		formatShare(accumulated) {
			const total = this.totalRow && this.totalRow.accumulated;
			if (!total || accumulated == null) return "—";
			return formatVariation(accumulated / total);
		},
		// Tooltip con HTML para preservar el color semántico del crecimiento.
		// Usa floating-vue (`html: true`) y consume los tokens de color como
		// inline-style — las vars de :root se resuelven dentro del portal del
		// tooltip aunque viva fuera del subárbol scoped. Para `pct == null`
		// (clientes nuevos, sin período anterior) igual mostramos el tooltip
		// con "—" para dar feedback al hover en todas las filas.
		growthTooltip(pct) {
			const text = this.formatPct(pct);
			let color = "var(--report-text-muted)";
			if (pct != null && pct > 0) color = "var(--report-positive)";
			else if (pct != null && pct < 0) color = "var(--report-negative)";
			return {
				content: `Crecimiento: <span style="color:${color};font-weight:600;">${text}</span>`,
				html: true,
			};
		},
		// El payload del evento `cell-click` lleva la row entera para que el
		// padre decida cómo abrir el drill-down (L2=cuenta → group_by=cuentas,
		// L3=cliente → drill por cliente + parent). Mantiene compat con el
		// modo plano: `row` simplemente no trae `level`. Normalizamos campos
		// ausentes a null para que el payload sea uniforme en todas las
		// variantes (plano, jerárquico L2/L3, total row).
		onCellClick(monthLabel, row) {
			if (!this.cellsClickable) return;
			this.$emit("cell-click", {
				monthLabel,
				account: row ? row.label : null,
				level: row && row.level != null ? row.level : null,
				parent: row && row.parent != null ? row.parent : null,
			});
		},
	},

	watch: {
		monthHeaders() {
			this.scrollToRight();
			// Cuando cambia el periodo, los account names del breakdown pueden
			// cambiar y el thead se desmonta-remonta — re-attachear el observer.
			this.$nextTick(() => {
				this.measureFirstCol();
				this.attachResizeObserver();
			});
		},
		isLoading(val) {
			if (!val) {
				this.scrollToRight();
				this.$nextTick(() => {
					this.measureFirstCol();
					this.attachResizeObserver();
				});
			}
		},
	},
};
</script>

<style scoped lang="scss">
.container_section {
	background: var(--report-bg);
	border-radius: 16px;
	border: 1px solid var(--report-border);
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.tittle_section {
	font-size: 18px;
	font-weight: 600;
	color: var(--report-text);
}

.action_btn {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	padding: 6px 12px;
	font-size: 14px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	display: flex;
	align-items: center;
	cursor: pointer;

	&:hover {
		background: var(--report-surface);
	}
}

.table_container {
	// Scroll en ambos ejes: x para el ancho del periodo, y para listas largas.
	overflow: auto;
	border: 1px solid var(--report-border);
	border-radius: 12px;
	background: var(--report-bg);
	max-width: 100%;
	// Alto máx ≈ thead (44) + 12 filas (12*44) + totales (44) = 616px.
	// max-height (no height) → si hay menos de 12 filas, la tabla se ajusta.
	max-height: calc(44px * 12);
	// Aísla este subárbol del resto del documento — repaints/reflows no se propagan.
	contain: layout paint;
}

// Primera columna sticky: ancho flexible. El <col width: 200px> queda como
// hint inicial en auto-layout (puede crecer si el contenido es más largo).
.sticky-column {
	position: sticky;
	left: 0;
	z-index: 2;
	background-color: inherit;
	border-right: 1px solid var(--report-border);
}

// Segunda columna sticky (Acumulado): el offset `left` se calcula en runtime
// midiendo el ancho real de la primera columna con ResizeObserver y
// exponiéndolo como --sticky-col-1-width en el style inline de la <table>.
// El fallback 200px coincide con el hint del <col> para el primer paint.
.sticky-column--acc {
	left: var(--sticky-col-1-width, 200px);
	box-sizing: border-box;
	// width: 160px;
	// min-width: 160px;
	// max-width: 160px;
}

// Celda apilada: monto arriba (énfasis), pct abajo (caption con flecha).
// Display block en los spans para forzar el wrap vertical sin <br>.
.acc_cell {
	.acc_amount {
		display: block;
		color: var(--report-text);
		font-size: 14px;
		font-weight: 500;
		line-height: 18px;
	}

	.acc_pct {
		display: block;
		font-size: 12px;
		font-weight: 500;
		line-height: 16px;
		margin-top: 2px;
	}

	.acc_pct--neutral { color: var(--report-text-muted); }
}

.custom_table {
	min-width: 100%;
	border-collapse: separate;
	border-spacing: 0;

	tbody tr {
		// Cada fila aislada: cambios en una fila no fuerzan layout del resto.
		contain: layout style;
	}

	td,
	th {
		height: 44px;
		padding: 12px 16px !important;
		border-bottom: 1px solid var(--report-divider-soft);
		vertical-align: middle;
	}

	thead th,
	td {
		text-align: end;
	}

	thead th {
		padding: 12px 24px;
		color: var(--report-text-muted);
		font-size: 12px;
		font-weight: 700;
		line-height: 18px;
		background-color: var(--report-surface);
		// Sticky-top: el encabezado queda visible al scrollear verticalmente.
		// El background-color es indispensable: como sticky pinta fuera del
		// flujo normal, sin él se vería el contenido del body atrás.
		position: sticky;
		top: 0;
		z-index: 3;
	}

	tbody th {
		color: var(--report-text);
		font-size: 12px;
		font-weight: 500;
		line-height: 18px;
	}

	tbody td {
		color: var(--report-text-muted-strong);
		font-size: 14px;
		font-weight: 400;
		line-height: 20px;
	}

	.theme_row_white {
		background-color: var(--report-bg);
	}

	// Affordance de drill-down: solo aplica a celdas mensuales de filas de
	// datos (no totales). El selector :hover sobre <td> mantiene el cambio
	// localizado a la celda apuntada — no toda la fila.
	.clickable_cell {
		cursor: pointer;
		transition: background-color 0.12s ease;

		&:hover {
			background-color: var(--report-accent-bg);
		}
	}

	.header_label {
		background-color: var(--report-surface);
	}

	.theme_row_azure {
		background-color: var(--report-row-azure);
		font-weight: 600;

		// Sticky-bottom: la fila de totales (Ventas Netas) queda anclada al
		// borde inferior del scroll-container. Aplicado a las celdas (no al
		// <tr>) porque algunos engines no soportan sticky en filas de tabla.
		th, td {
			position: sticky;
			bottom: 0;
			z-index: 3;
		}
	}

	// Esquinas: las celdas que son sticky en AMBOS ejes (top + left) deben
	// pintar por encima de los stickies de un solo eje, por eso suben a z-index
	// 4. Incluye la nueva columna "Acumulado" (sticky-column--acc) tanto en el
	// thead como en la fila de totales (theme_row_azure).
	thead .sticky-column,
	thead .sticky-column--acc,
	.theme_row_azure .sticky-column,
	.theme_row_azure .sticky-column--acc {
		z-index: 4;
	}

	// Modo jerárquico (clientes anidados bajo cuenta).
	// L2: cuenta padre, con énfasis visual. L3: cliente hijo, indented + muted.
	// Copia conceptual del patrón de ExpensesEvolutionTable.
	.row-level-2 {
		font-weight: 700;
		background: var(--report-row-emphasis);
	}

	.row-level-3 {
		th, td {
			font-size: 12.5px !important;
			color: var(--report-text-muted) !important;
		}
	}

	.pl-5 { padding-left: 32px !important; }

	.indent-symbol {
		color: var(--report-text-muted-soft);
		font-weight: 400;
	}

	.label_inner {
		width: 100%;
	}

	.chevron-btn {
		background: transparent;
		border: none;
		padding: 4px;
		display: inline-flex;
		align-items: center;
		cursor: pointer;
		border-radius: 4px;

		&:hover { background: var(--report-surface); }
		&:focus-visible { outline: 2px solid var(--report-accent-border); outline-offset: -2px; }
	}

	.expand-icon {
		width: 14px;
		height: 14px;
		transition: transform 0.2s ease;
	}

	.rotate-icon { transform: rotate(180deg); }
}

.loading_state,
.empty_state,
.error_state {
	color: var(--report-text-muted);
	font-size: 14px;
}

.nobreak {
	white-space: nowrap;
}
</style>
